import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Image, Edit3, LogOut } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContentEditorPage = () => {
  const navigate = useNavigate();
  const { requireAuth, logout, getAuthHeaders } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [content, setContent] = useState({
    hero_title: "",
    hero_author: "",
    hero_author_photo_url: "",
    hero_subtitle: "",
    hero_description: "",
    hero_image_url: "",
    gallery_images: [],
    youtube_url: "",
    facebook_url: "",
    price_amount: 5,
    price_currency: "USD",
    benefit_1_title: "",
    benefit_1_description: "",
    benefit_2_title: "",
    benefit_2_description: "",
    benefit_3_title: "",
    benefit_3_description: "",
    testimonial_text: "",
    testimonial_author: ""
  });

  useEffect(() => {
    if (!requireAuth()) return;

    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement du contenu", error);
        if (error.response?.status === 403) {
          toast.error("Session expirée. Veuillez vous reconnecter.");
          logout();
        }
      } finally {
        setFetching(false);
      }
    };

    fetchContent();
  }, [requireAuth, logout]);

  const handleChange = (field, value) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data"
        }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      handleChange("hero_image_url", imageUrl);
      toast.success("Image uploadée avec succès !");
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'upload";
      toast.error(message);
      if (error.response?.status === 403) {
        logout();
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/admin/content`, content, {
        headers: getAuthHeaders()
      });
      toast.success("Contenu sauvegardé avec succès ! Le site est à jour.");
    } catch (error) {
      const message = error?.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(message);
      if (error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Éditeur de Contenu</h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/config")}
            >
              Config Bkapay
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/orders")}
            >
              Commandes
            </Button>
            <Button
              variant="destructive"
              onClick={logout}
              size="sm"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section Hero */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5" />
                Section Principale
              </CardTitle>
              <CardDescription>
                Titre, auteur, description et image de couverture
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Auteur</Label>
                <Input
                  value={content.hero_author}
                  onChange={(e) => handleChange("hero_author", e.target.value)}
                  placeholder="Pasteur Joseph Kalala"
                />
              </div>

              <div>
                <Label>Photo de profil de l'auteur</Label>
                <div className="space-y-4 mt-2">
                  {content.hero_author_photo_url && (
                    <div className="flex items-center gap-3">
                      <img
                        src={content.hero_author_photo_url}
                        alt="Auteur"
                        className="w-24 h-24 object-cover rounded-full border-4 border-orange-200"
                      />
                      <p className="text-sm text-green-600 font-semibold">✓ Photo sélectionnée</p>
                    </div>
                  )}
                  
                  {/* Option 1: Choose from gallery */}
                  {content.gallery_images && content.gallery_images.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">📸 Option 1 : Choisir depuis votre galerie</p>
                      <div className="grid grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg">
                        {content.gallery_images.map((url, index) => (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange("hero_author_photo_url", url);
                              toast.success("Photo de profil mise à jour !");
                            }}
                            className="relative cursor-pointer group hover:ring-2 hover:ring-orange-500 rounded-lg transition-all"
                          >
                            <img 
                              src={url} 
                              alt={`Galerie ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-all">
                              <span className="text-white text-xs opacity-0 group-hover:opacity-100 font-semibold">Utiliser</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Cliquez sur une image pour l'utiliser comme photo de profil</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm font-medium mb-2">🔗 Option 2 : Coller le lien de l'image</p>
                    <Input
                      type="url"
                      value={content.hero_author_photo_url}
                      onChange={(e) => handleChange("hero_author_photo_url", e.target.value)}
                      placeholder="https://example.com/votre-photo.jpg"
                    />
                    <p className="text-xs text-gray-500 mt-1">Collez l'URL de votre photo</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium mb-2">💻 Option 3 : Uploader depuis votre ordinateur</p>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploading(true);
                        const formData = new FormData();
                        formData.append("file", file);
                        try {
                          const response = await axios.post(`${API}/admin/upload-image`, formData, {
                            headers: {
                              ...getAuthHeaders(),
                              "Content-Type": "multipart/form-data"
                            }
                          });
                          const imageUrl = `${BACKEND_URL}${response.data.url}`;
                          handleChange("hero_author_photo_url", imageUrl);
                          toast.success("Photo uploadée !");
                        } catch (error) {
                          toast.error("Erreur upload");
                          if (error.response?.status === 403) logout();
                        } finally {
                          setUploading(false);
                        }
                      }}
                      disabled={uploading}
                    />
                    {uploading && <p className="text-sm text-gray-500 mt-1">Upload en cours...</p>}
                  </div>
                </div>
              </div>

              <div>
                <Label>Titre du livre</Label>
                <Input
                  value={content.hero_title}
                  onChange={(e) => handleChange("hero_title", e.target.value)}
                  placeholder="Pasteur Joseph Kalala - Le Mystère de la Croix"
                />
              </div>

              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={content.hero_subtitle}
                  onChange={(e) => handleChange("hero_subtitle", e.target.value)}
                  placeholder="L'espérance qui change tout"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={content.hero_description}
                  onChange={(e) => handleChange("hero_description", e.target.value)}
                  placeholder="Description du livre..."
                  rows={4}
                />
              </div>

              <div>
                <Label>Image de couverture</Label>
                <div className="space-y-4 mt-2">
                  {content.hero_image_url && (
                    <div className="flex items-center gap-3">
                      <img
                        src={content.hero_image_url}
                        alt="Couverture"
                        className="w-32 h-40 object-cover rounded border-4 border-orange-200"
                      />
                      <p className="text-sm text-green-600 font-semibold">✓ Couverture sélectionnée</p>
                    </div>
                  )}
                  
                  {/* Option 1: Choose from gallery */}
                  {content.gallery_images && content.gallery_images.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">📸 Option 1 : Choisir depuis votre galerie</p>
                      <div className="grid grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg">
                        {content.gallery_images.map((url, index) => (
                          <div
                            key={index}
                            onClick={() => {
                              handleChange("hero_image_url", url);
                              toast.success("Couverture mise à jour !");
                            }}
                            className="relative cursor-pointer group hover:ring-2 hover:ring-orange-500 rounded-lg transition-all"
                          >
                            <img 
                              src={url} 
                              alt={`Galerie ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-all">
                              <span className="text-white text-xs opacity-0 group-hover:opacity-100 font-semibold">Utiliser</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Cliquez sur une image pour l'utiliser comme couverture</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm font-medium mb-2">🔗 Option 2 : Coller le lien de l'image</p>
                    <Input
                      type="url"
                      value={content.hero_image_url}
                      onChange={(e) => handleChange("hero_image_url", e.target.value)}
                      placeholder="https://example.com/couverture.jpg"
                    />
                    <p className="text-xs text-gray-500 mt-1">Collez l'URL de la couverture</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium mb-2">💻 Option 3 : Uploader depuis votre ordinateur</p>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                    {uploading && <p className="text-sm text-gray-500 mt-1">Upload en cours...</p>}
                  </div>
                </div>
              </div>

              <div>
                <Label>Prix</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={content.price_amount}
                    onChange={(e) => handleChange("price_amount", parseInt(e.target.value))}
                    placeholder="5"
                    className="flex-1"
                  />
                  <select
                    value={content.price_currency}
                    onChange={(e) => handleChange("price_currency", e.target.value)}
                    className="border rounded px-3 py-2"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="CDF">CDF (FC)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Galerie d'images supplémentaires */}
          <Card>
            <CardHeader>
              <CardTitle>Galerie d'Images</CardTitle>
              <CardDescription>
                Uploadez des images supplémentaires. Copiez les URLs pour les utiliser dans vos chapitres ou ailleurs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Ajouter une image à la galerie</Label>
                <div className="mt-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploading(true);
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const response = await axios.post(`${API}/admin/upload-image`, formData, {
                          headers: {
                            ...getAuthHeaders(),
                            "Content-Type": "multipart/form-data"
                          }
                        });
                        const imageUrl = `${BACKEND_URL}${response.data.url}`;
                        const newGallery = [...(content.gallery_images || []), imageUrl];
                        handleChange("gallery_images", newGallery);
                        toast.success("Image ajoutée à la galerie !");
                        e.target.value = "";
                      } catch (error) {
                        toast.error("Erreur upload");
                        if (error.response?.status === 403) logout();
                      } finally {
                        setUploading(false);
                      }
                    }}
                    disabled={uploading}
                  />
                  {uploading && <p className="text-sm text-gray-500 mt-1">Upload en cours...</p>}
                </div>
              </div>

              {content.gallery_images && content.gallery_images.length > 0 && (
                <div className="mt-4">
                  <Label>Images uploadées ({content.gallery_images.length})</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                    {content.gallery_images.map((url, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={url} 
                          alt={`Image ${index + 1}`}
                          className="w-full h-32 object-cover rounded border"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(url);
                              toast.success("URL copiée !");
                            }}
                            className="text-white text-xs bg-blue-600 px-3 py-1 rounded"
                          >
                            Copier URL
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const newGallery = content.gallery_images.filter((_, i) => i !== index);
                              handleChange("gallery_images", newGallery);
                              toast.success("Image supprimée");
                            }}
                            className="text-white text-xs bg-red-600 px-3 py-1 rounded"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Réseaux Sociaux */}
          <Card>
            <CardHeader>
              <CardTitle>📱 Réseaux Sociaux</CardTitle>
              <CardDescription>
                Ajoutez vos liens YouTube et Facebook pour que les visiteurs puissent vous suivre
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Lien YouTube (vidéo ou chaîne)</Label>
                <Input
                  type="url"
                  value={content.youtube_url}
                  onChange={(e) => handleChange("youtube_url", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemple: https://youtube.com/watch?v=ABC123 ou https://youtube.com/@votre-chaine
                </p>
              </div>

              <div>
                <Label>Lien Facebook (page ou profil)</Label>
                <Input
                  type="url"
                  value={content.facebook_url}
                  onChange={(e) => handleChange("facebook_url", e.target.value)}
                  placeholder="https://facebook.com/votrepage"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemple: https://facebook.com/votre-page ou https://facebook.com/votreprofil
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section Bénéfices */}
          <Card>
            <CardHeader>
              <CardTitle>Les 3 Bénéfices</CardTitle>
              <CardDescription>
                Pourquoi acheter ce livre ?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bénéfice 1 */}
              <div className="space-y-2 p-4 bg-gray-50 rounded">
                <h3 className="font-semibold">Bénéfice 1</h3>
                <Input
                  value={content.benefit_1_title}
                  onChange={(e) => handleChange("benefit_1_title", e.target.value)}
                  placeholder="Titre du bénéfice 1"
                />
                <Textarea
                  value={content.benefit_1_description}
                  onChange={(e) => handleChange("benefit_1_description", e.target.value)}
                  placeholder="Description..."
                  rows={2}
                />
              </div>

              {/* Bénéfice 2 */}
              <div className="space-y-2 p-4 bg-gray-50 rounded">
                <h3 className="font-semibold">Bénéfice 2</h3>
                <Input
                  value={content.benefit_2_title}
                  onChange={(e) => handleChange("benefit_2_title", e.target.value)}
                  placeholder="Titre du bénéfice 2"
                />
                <Textarea
                  value={content.benefit_2_description}
                  onChange={(e) => handleChange("benefit_2_description", e.target.value)}
                  placeholder="Description..."
                  rows={2}
                />
              </div>

              {/* Bénéfice 3 */}
              <div className="space-y-2 p-4 bg-gray-50 rounded">
                <h3 className="font-semibold">Bénéfice 3</h3>
                <Input
                  value={content.benefit_3_title}
                  onChange={(e) => handleChange("benefit_3_title", e.target.value)}
                  placeholder="Titre du bénéfice 3"
                />
                <Textarea
                  value={content.benefit_3_description}
                  onChange={(e) => handleChange("benefit_3_description", e.target.value)}
                  placeholder="Description..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Section Témoignage */}
          <Card>
            <CardHeader>
              <CardTitle>Témoignage Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Texte du témoignage</Label>
                <Textarea
                  value={content.testimonial_text}
                  onChange={(e) => handleChange("testimonial_text", e.target.value)}
                  placeholder="Ce livre a transformé ma vie..."
                  rows={4}
                />
              </div>

              <div>
                <Label>Nom du client</Label>
                <Input
                  value={content.testimonial_author}
                  onChange={(e) => handleChange("testimonial_author", e.target.value)}
                  placeholder="Marie K., Kinshasa"
                />
              </div>
            </CardContent>
          </Card>

          {/* Bouton Sauvegarder */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              size="lg"
            >
              <Save className="mr-2 h-5 w-5" />
              {loading ? "Sauvegarde..." : "Enregistrer et Publier"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminContentEditorPage;
